<template>
  <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
    <TemplateVariables
      v-if="showVariablesModal"
      @close="showVariablesModal = false"
    />
    <BaseSpinner v-if="loading" />
    <FiskalySettings
      v-else-if="hasFeatureFlag('fiskaly-sign')"
      @submit="submitForm"
    />
    <PageSplit v-else>
      <template #left>
        <BaseForm
          fullHeight
          :scrollContainer="scrollContainer"
          @submit="submitForm"
          @validationError="onValidationError"
        >
          <div :class="$style.formContainer">
            <div
              ref="scrollContainer"
              :class="$style.formContent"
            >
              <BaseHeading size="l">
                {{ hasFeatureFlag('compliant-register') ? $t('admin.invoices_and_receipts.title') : $t('admin.invoices.title') }}
              </BaseHeading>
              <BaseAccordion
                v-model="activeAccordionItems"
                :titles="accordionTitles"
                :routeQueries="accordionRouteQueries"
                @toggle="onSectionToggle"
              >
                <template #1>
                  <InvoiceSettings
                    v-if="hasFeatureFlag('compliant-register')"
                  />
                  <OldPermissions v-else />
                </template>
                <template #2>
                  <ReceiptSettings
                    v-if="hasFeatureFlag('compliant-register')"
                  />
                  <OldContent v-else />
                </template>
                <template
                  v-if="company.country === 'de'"
                  #3
                >
                  <DatevSettings />
                </template>
              </BaseAccordion>
            </div>
            <div :class="$style.formControls">
              <BaseButton
                :fullWidth="$screen === 's'"
                :loading="isLoading"
                submitForm
                v-test="'submitForm'"
              >
                {{ $t('global.actions.save') }}
              </BaseButton>
            </div>
          </div>
        </BaseForm>
      </template>
      <template #right>
        <Flex
          direction="column"
          align="center"
          justify="center"
        >
          <Preview
            :formData="formData"
            :mode="previewMode"
            :medical="isMedical"
          />
        </Flex>
      </template>
    </PageSplit>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { computed, nextTick, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { setScrollOnElement } from '@/helpers/scroll';
import { flash } from '@/helpers/ui';
import PageSplit from '@/modules/PageSplit.vue';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';

import DatevSettings from './DatevSettings.vue';
import FiskalySettings from './FiskalySettings.vue';
import InvoiceSettings from './InvoiceSettings.vue';
import OldContent from './OldContent.vue';
import OldPermissions from './OldPermissions.vue';
import Preview from './Preview.vue';
import ReceiptSettings from './ReceiptSettings.vue';
import TemplateVariables from './TemplateVariables.vue';
import { useInvoices } from './useInvoices';

const {
  formData,
  currentImageUrl,
  hasPaymentProvider,
  hasPaymentLink,
  isMedical,
  loading,
} = useInvoices();
const { company, updateCompany } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();
const { t } = useI18n();

const scrollContainer = ref();
const activeAccordionItems = ref<string[]>(['1']);
const previewMode = computed(() =>
  activeAccordionItems.value.includes('2') &&
  !activeAccordionItems.value.includes('1') &&
  hasFeatureFlag('compliant-register')
    ? 'receipt'
    : 'invoice',
);
const isLoading = ref(false);
const showVariablesModal = ref(false);

const accordionTitles = computed(() =>
  hasFeatureFlag('compliant-register')
    ? [
        t('admin.invoices_and_receipts.invoice_settings.title'),
        t('admin.invoices_and_receipts.receipt_settings.title'),
        t('admin.invoices.datev.title'),
      ]
    : [
        t('admin.invoices.permissions.title'),
        t('admin.invoices.content.title'),
        t('admin.invoices.datev.title'),
      ],
);

const accordionRouteQueries = computed(() =>
  hasFeatureFlag('compliant-register')
    ? ['invoice-settings', 'receipt-settings', 'date-settings']
    : ['permissions', 'invoice-content', 'date-settings'],
);

provide('formData', formData);
provide('showVariablesModal', showVariablesModal);
provide('currentImageUrl', currentImageUrl);
provide('hasPaymentProvider', hasPaymentProvider);
provide('hasPaymentLink', hasPaymentLink);
provide('isLoading', isLoading);

const onSectionToggle = (element: HTMLElement) => {
  nextTick(() => {
    if (activeAccordionItems.value.length && element) {
      setScrollOnElement(element);
    }
  });
};

const submitForm = () => {
  isLoading.value = true;
  updateCompany(formData)
    .then(() => {
      flash(t('global.flash.company_updated'));
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onValidationError = () => {
  activeAccordionItems.value = ['1', '2', '3'];
};
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}

.formContainer {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.formContent {
  display: flex;
  flex-direction: column;
  gap: $spacing;
  flex-grow: 1;
  overflow-y: scroll;
}

.formControls {
  display: flex;
  justify-content: flex-end;
  padding: $spacing;
  background-color: var(--color-bg);
  border-top: 1px solid var(--color-border);

  .base.smallScreen & {
    padding: $spacing 0;
  }
}
</style>
